import { createContext } from "preact";
import { useContext, useMemo, useState, useEffect } from "preact/hooks";
import axios from "axios";
import { navigate } from '../utils/Navigator';
import { persistSession, clearSession, useUserSession } from "../utils";
const isProd = import.meta.env.PROD;
const defaultEndpoints = {
  askOtp: (role, key, id) => 
     `${isProd ? "" : "http://localhost:3000"}/auth/login/otp/${role}/${key}/${encodeURIComponent(id)}`,
  verifyOtp:  `${isProd ? "" : "http://localhost:3000"}/auth/login/otp/verify`,
  ssoLogin: (appId) => `${isProd ? "" : "http://localhost:3000"}/auth/sso/login?appId=${encodeURIComponent(appId || 'adevs-admin')}`,
  logout:  `${isProd ? "" : "http://localhost:3000"}/auth/session/logout?redirect=/adevs-admin/login`,
};


export const LoginStoreContext = createContext(null);

export function useLoginStore() {
  const ctx = useContext(LoginStoreContext);
  if (!ctx) {
    throw new Error("useLoginStore must be used within <LoginStoreProvider/>");
  }
  return ctx;
}

export function LoginStoreProvider(
  props
) {
  const UserSession = useUserSession();
  if(UserSession.raw && UserSession.raw.ok){
      window.location.href = (`/${UserSession.raw.type}/dashboard`);
  }

  const { role, endpoints, onSuccess, redirectTo, children } = props;
  const ep = useMemo(() => ({ ...defaultEndpoints, ...endpoints }), [endpoints]);

  const [loginKey, setLoginKey] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Extraer token del callback SSO (?token=... en la URL)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      persistSession(token, null, [], { authProvider: "sso" });
      // Limpiar token del URL para que no quede en historial
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      window.history.replaceState({}, "", url.toString());
      // Redirigir al dashboard
      const appId = role === "adevs-client" ? "adevs-client" : "adevs-admin";
      window.location.href = `/${appId}/dashboard`;
    }
  }, [role]);

  const loginWithSSO = () => {
    const appId = role === 'adevs-client' ? 'adevs-client' : 'adevs-admin';
    window.location.href = ep.ssoLogin(appId);
  };

  const askOtp = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.get(
        ep.askOtp(role, loginKey, identifier), 
        { validateStatus: () => true }
      );
      if (data?.ok) {
        setOtp({ 
          ok: true, 
          value: identifier, 
          message: data.message, 
          expiresAt: data.expiresAt, 
          code: "" 
        });
      } else {
        setError((data?.error || data?.message) ?? "No se pudo generar el código.");
      }
    } catch (e) {
      setError((e?.response?.data?.error || e?.response?.data?.message) ?? "Error solicitando el código.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp?.code?.trim()) {
      setError("Ingresa el código OTP.");
      return;
    }
    if (!identifier.trim()) {
      setError(loginKey === "email" ? "Ingresa tu email." : "Ingresa tu teléfono.");
      return;
    }

    setError("");
    setVerifying(true);
    
    try {
      const payload = loginKey === "email"
        ? { email: identifier.trim(), otp: otp.code.trim(), role }
        : { phone: identifier.trim(), otp: otp.code.trim(), role };

      const res = await axios.post(ep.verifyOtp, payload, {
        headers: { "Content-Type": "application/json" },
        validateStatus: () => true,
      });
      
      const { data, status } = res;

      if (data?.ok) {
        // Guardar JWT token del SSO
        const token = data.token || data.sessionId;
        persistSession(
          token,
          data.userId,
          Array.isArray(data.roles) ? data.roles : undefined,
          { authProvider: 'sso' }
        );
        
        if (onSuccess) {
          onSuccess(data);
        } else if (redirectTo) {
          window.location.href = redirectTo;
        }
        return;
      }

      switch (status) {
        case 401:
          setError((data?.error || data?.message) ?? "Sesión expirada o inválida.");
          clearSession();
          break;
        case 409:
          setError((data?.error || data?.message) ?? "Código inválido o expirado.");
          break;
        case 400:
          setError((data?.error || data?.message) ?? "Solicitud inválida.");
          break;
        default:
          setError((data?.error || data?.message) ?? "No fue posible verificar el código.");
          break;
      }
    } catch (e) {
      setError((e?.response?.data?.error || e?.response?.data?.message) ?? "Error al verificar el código.");
    } finally {
      setVerifying(false);
    }
  };

  const value= {
    loginKey,
    identifier,
    otp,
    error,
    loading,
    verifying,
    setLoginKey,
    setIdentifier,
    setOtp,
    setError,
    askOtp,
    verifyOtp,
    loginWithSSO
  };

  return <LoginStoreContext.Provider value={value}>{children}</LoginStoreContext.Provider>;
}
